/**
 * The insertion surface: one right-panel mode for both sections and elements.
 *
 * Section entries show a wireframe miniature rather than an icon and a name.
 * A church volunteer choosing "Hero - Modern Glow" over "Hero - Split" is
 * making a visual decision, and a row of identical icons gives them nothing
 * to decide with.
 */
import React, { useState } from 'react';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

import { BLOCK_DEFINITIONS } from '../blocks/registry';
import { PRESET_CATEGORIES, SECTION_PRESETS, type PresetCategory, type SectionPreset } from '../sectionPresets';
import type { BlockType } from '../types';

export type AddLibraryMode = 'ADD_SECTION' | 'ADD_ELEMENT';

interface AddLibraryProps {
  mode: AddLibraryMode;
  onModeChange?: (mode: AddLibraryMode) => void;
  onClose: () => void;
  onSelectSection: (preset: SectionPreset) => void;
  onSelectElement: (type: BlockType) => void;
}

export function AddLibrary({ mode, onModeChange, onClose, onSelectSection, onSelectElement }: AddLibraryProps) {
  const [category, setCategory] = useState<PresetCategory>('Hero');
  const [activeMode, setActiveMode] = useState<AddLibraryMode>(mode);
  const [query, setQuery] = useState('');
  const selectMode = (next: AddLibraryMode) => { setActiveMode(next); onModeChange?.(next); };
  const normalizedQuery = query.trim().toLowerCase();
  const visibleSections = SECTION_PRESETS.filter((preset) => preset.category === category && (!normalizedQuery || `${preset.label} ${preset.description}`.toLowerCase().includes(normalizedQuery)));
  const visibleElements = BLOCK_DEFINITIONS.filter((definition) => !normalizedQuery || `${definition.label} ${definition.description}`.toLowerCase().includes(normalizedQuery));

  return (
    <aside className="flex h-full w-full flex-col border-l border-slate-200 bg-white">
      <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 pb-3 pt-4">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.16em] text-slate-500">
            Toevoegen
          </h2>
          <p className="mt-1 text-xs text-slate-500">
            Kies visuele inhoud voor de geselecteerde plek.
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Sluiten"
          className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex gap-1 border-b border-slate-100 px-5 py-3">
        {([['ADD_SECTION', 'Secties'], ['ADD_ELEMENT', 'Elementen']] as const).map(([value, label]) => (
          <button key={value} type="button" onClick={() => selectMode(value)} className={cn('rounded-lg px-3 py-1.5 text-xs font-semibold transition', activeMode === value ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-100')}>{label}</button>
        ))}
      </div>
      <label className="mx-5 mt-3 block"><span className="sr-only">Zoeken in bibliotheek</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Zoek een sectie of element…" className="w-full rounded-xl border border-slate-300 px-3.5 py-2.5 text-sm outline-none transition focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20" /></label>

      {activeMode === 'ADD_SECTION' ? (
        <>
          <div className="flex flex-wrap gap-1.5 border-b border-slate-100 px-5 py-3">
            {PRESET_CATEGORIES.map((entry) => (
              <button
                key={entry}
                type="button"
                onClick={() => setCategory(entry)}
                className={cn(
                  'rounded-lg px-2.5 py-1.5 text-xs font-semibold transition',
                  category === entry ? 'bg-teal-50 text-teal-800' : 'text-slate-600 hover:bg-slate-100',
                )}
              >
                {entry}
              </button>
            ))}
          </div>
          <div className="flex-1 space-y-2 overflow-y-auto p-5">
            {visibleSections.map((preset) => (
              <button
                key={preset.id}
                type="button"
                onClick={() => onSelectSection(preset)}
                className="w-full rounded-xl border border-slate-200 p-2.5 text-left transition hover:border-teal-300 hover:bg-teal-50/40"
              >
                <PresetMini preview={preset.preview} />
                <span className="mt-2 block text-sm font-bold text-slate-900">{preset.label}</span>
                <span className="block text-xs leading-relaxed text-slate-500">{preset.description}</span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-2 gap-2">
            {visibleElements.map((definition) => {
              const Icon = definition.icon;
              return (
                <button
                  key={definition.type}
                  type="button"
                  onClick={() => onSelectElement(definition.type)}
                  className="flex flex-col items-start gap-1.5 rounded-xl border border-slate-200 p-3 text-left transition hover:border-teal-300 hover:bg-teal-50/40"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                    <Icon className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-semibold text-slate-900">{definition.label}</span>
                  <span className="text-xs leading-snug text-slate-500">{definition.description}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
}

/** Wireframe of a section's shape. Bars stand for text, blocks for media,
 * the teal pill for a call to action. */
function PresetMini({ preview }: { preview: SectionPreset['preview'] }) {
  const bar = 'rounded-full bg-slate-300';
  const dark = preview === 'hero-glow' || preview === 'cta';

  const body = () => {
    switch (preview) {
      case 'hero-glow':
      case 'hero-split':
        return (
          <span className="flex h-full w-full items-center gap-2">
            <span className="flex flex-1 flex-col gap-1">
              <span className={cn(bar, 'h-1 w-8 bg-teal-400')} />
              <span className={cn(bar, 'h-2 w-full', dark && 'bg-white/70')} />
              <span className={cn(bar, 'h-1 w-3/4', dark && 'bg-white/40')} />
              <span className="mt-1 flex gap-1">
                <span className="h-2.5 w-8 rounded bg-teal-400" />
                <span className={cn('h-2.5 w-8 rounded border', dark ? 'border-white/50' : 'border-slate-300')} />
              </span>
            </span>
            <span className={cn('h-full w-1/3 rounded', dark ? 'bg-white/15' : 'bg-slate-200')} />
          </span>
        );
      case 'hero-center':
        return (
          <span className="flex h-full w-full flex-col items-center justify-center gap-1">
            <span className={cn(bar, 'h-1 w-6 bg-teal-400')} />
            <span className={cn(bar, 'h-2 w-3/4')} />
            <span className={cn(bar, 'h-1 w-1/2')} />
            <span className="mt-1 h-2.5 w-10 rounded bg-teal-400" />
          </span>
        );
      case 'cards-3':
      case 'cards-4': {
        const count = preview === 'cards-4' ? 4 : 3;
        return (
          <span className="flex h-full w-full flex-col gap-1.5">
            <span className={cn(bar, 'h-1.5 w-1/3')} />
            <span className="flex flex-1 gap-1">
              {Array.from({ length: count }, (_, index) => (
                <span key={index} className="flex-1 rounded bg-slate-200" />
              ))}
            </span>
          </span>
        );
      }
      case 'split-left':
      case 'split-right': {
        const media = <span className="h-full w-1/3 rounded bg-slate-200" />;
        const text = (
          <span className="flex flex-1 flex-col justify-center gap-1">
            <span className={cn(bar, 'h-1.5 w-3/4')} />
            <span className={cn(bar, 'h-1 w-full')} />
            <span className={cn(bar, 'h-1 w-2/3')} />
          </span>
        );
        return (
          <span className="flex h-full w-full items-stretch gap-2">
            {preview === 'split-left' ? media : text}
            {preview === 'split-left' ? text : media}
          </span>
        );
      }
      case 'cta':
        return (
          <span className="flex h-full w-full flex-col items-center justify-center gap-1">
            <span className={cn(bar, 'h-1.5 w-1/2', dark && 'bg-white/70')} />
            <span className={cn(bar, 'h-1 w-2/3', dark && 'bg-white/40')} />
            <span className="mt-1 h-2.5 w-10 rounded bg-teal-400" />
          </span>
        );
      case 'contact':
        return (
          <span className="flex h-full w-full gap-2">
            <span className="flex flex-1 flex-col gap-1">
              <span className={cn(bar, 'h-1.5 w-2/3')} />
              <span className={cn(bar, 'h-1 w-full')} />
              <span className={cn(bar, 'h-1 w-1/2')} />
            </span>
            <span className="flex flex-1 flex-col gap-1">
              <span className={cn(bar, 'h-1 w-full')} />
              <span className={cn(bar, 'h-1 w-full')} />
              <span className="mt-auto h-2.5 w-12 rounded border border-slate-300" />
            </span>
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-20 w-full rounded-lg p-2.5 ring-1 ring-inset',
        dark ? 'bg-slate-800 ring-slate-700' : 'bg-white ring-slate-200',
      )}
    >
      {body()}
    </span>
  );
}
